import type { RenderedView } from '../router.js';
import { navigate } from '../router.js';
import { repos } from '../store.js';
import { esc, onClick, formDialog, toast, confirmDialog } from '../dom.js';
import { icon } from '../icons.js';
import { importStudentsDialog } from '../studentImport.js';
import { studentFormDialog } from '../studentForm.js';
import { classFormDialog } from '../classForm.js';

// Bulk paste: lines of "LRN, Name[, M|F]" — the 12-digit LRN is the student's identity.
async function bulkForm(classId: string) {
  const v = await formDialog(
    'Bulk add students',
    [{ name: 'rows', label: 'One per line: LRN, Name, Sex(optional)', type: 'textarea', required: true, hint: 'e.g. 136800120001, Juan Santos, M' }],
    'Add all',
  );
  if (!v) return;
  const rows = v.rows
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const [lrn, name, sex] = l.split(',').map((s) => s.trim());
      return { lrn: lrn ?? '', name: name ?? '', sex: (sex as any) || '' };
    });
  const bad = rows.find((r) => !/^\d{12}$/.test(r.lrn) || !r.name);
  if (bad) return toast(`Check line: "${bad.lrn} ${bad.name}" (need 12-digit LRN + name)`, 'err');
  try {
    const added = await repos.addStudents(classId, rows);
    toast(`Added ${added.length} students`);
    navigate(`#/class/${classId}`);
  } catch (e: any) {
    toast(e.message, 'err');
  }
}

export async function classDetailView(params: Record<string, string>): Promise<RenderedView> {
  const cls = await repos.getClass(params.id);
  if (!cls) return { title: 'Class', back: '/classes', html: `<div class="view"><p class="empty">Class not found.</p></div>` };
  const [students, impact] = await Promise.all([repos.studentsInClass(cls.id), repos.classDeletionImpact(cls.id)]);

  const rows = students.length
    ? `<ul class="list">${students
        .map(
          (s) => `<button class="row" data-student="${esc(s.id)}"><span class="grow"><span class="ttl">${esc(s.name)}</span>
          <span class="meta">LRN <span class="mono">${esc(s.lrn)}</span>${s.sex ? ' · ' + esc(s.sex) : ''}</span></span><span class="chev">${icon.edit()}</span></button>`,
        )
        .join('')}</ul>`
    : `<div class="empty">${icon.users()}<p>No students yet.</p></div>`;

  return {
    title: cls.name,
    subtitle: `Grade ${cls.gradeLevel} · ${cls.schoolYear} · ${students.length} students`,
    back: '/classes',
    // Edit name / grade level / school year. Re-navigating to the same hash
    // re-renders the view, so the app bar picks up the new values.
    action: { label: 'Edit', icon: icon.edit(), onClick: () => classFormDialog({ existing: cls, onDone: () => navigate(`#/class/${cls.id}`) }) },
    html: `<div class="view">
      <div class="row2"><button class="btn btn-ghost" data-add>${icon.plus()} Add student</button>
      <button class="btn btn-ghost" data-import>${icon.doc()} Import CSV</button></div>
      <button class="btn btn-ghost btn-block" data-bulk style="margin-top:10px">${icon.list()} Bulk paste</button>
      <div style="height:12px"></div>${rows}
      <p class="section-label" style="margin-top:22px">Danger zone</p>
      <button class="btn btn-danger btn-block" data-delete>${icon.trash()} Delete this class</button>
      <p class="hint">Removes the class, its ${impact.students} student${impact.students === 1 ? '' : 's'}${impact.assignments ? `, ${impact.assignments} quiz assignment${impact.assignments === 1 ? '' : 's'} and ${impact.results} scanned result${impact.results === 1 ? '' : 's'}` : ''}. Quizzes stay. This cannot be undone.</p>
    </div>`,
    mount(root) {
      onClick(root, '[data-add]', () => studentFormDialog({ classId: cls.id, onDone: () => navigate(`#/class/${cls.id}`) }));
      onClick(root, '[data-import]', () => importStudentsDialog(cls.id));
      onClick(root, '[data-bulk]', () => bulkForm(cls.id));
      onClick(root, '[data-student]', (el) => navigate(`#/student/${el.dataset.student}`));
      onClick(root, '[data-delete]', async () => {
        if (!confirmDialog(`Delete "${cls.name}"? Its ${impact.students} student${impact.students === 1 ? '' : 's'}${impact.assignments ? `, ${impact.assignments} quiz assignment${impact.assignments === 1 ? '' : 's'} and ${impact.results} scanned result${impact.results === 1 ? '' : 's'}` : ''} are deleted too. This cannot be undone.`)) return;
        // Scanned results are the only record of a sitting — confirm again if any exist.
        if (impact.results && !confirmDialog(`Really delete ${impact.results} scanned result${impact.results === 1 ? '' : 's'}? Export any analysis you still need first.`)) return;
        await repos.deleteClass(cls.id);
        toast('Class deleted');
        navigate('#/classes');
      });
    },
  };
}
